import { useState, useEffect } from "react";
import AuthLayout from "../../../components/layouts/auth";
import ApiRequest from "../../../libs/ApiCall";
import AddTransaction from "./add-transaction";
import moment from "moment";
import debounce from 'lodash.debounce';
import { Minus } from "lucide-react";
import { formatNumber, getCurrency, numberWithCommas, SmoothScroll } from "../../../libs/helper-support";
import PaginationType from "../../../types/pagination-type";
import InputField from "../../../components/form/input-field";
import TransactionType from "../../../types/transaction-type";
import EditTransaction from "./edit-transaction";
import DefaultModal from "../../../components/modal/default-modal";
import { ErrorFlash, SuccessFlash } from "../../../libs/flash-notification";

const Transactions = () => {

    // {uuid}/update or {uuid}/delete
    const [transactions, setTransactions] = useState<TransactionType[]>([]);

    const [pageLoader, setPageLoader] = useState<boolean>(false);

    const [pageData, setPageData] = useState<PaginationType>({} as PaginationType);
    const [page, setPage] = useState<number>(1);

    const [search, setSearch] = useState<string>('');

    const [loadingPageData, setLoadingPageData] = useState<boolean>(false);

    const getTransactions = async () => {
        setLoadingPageData(true);
        const request = await ApiRequest({endpoint: `/transaction?page=${page}`, method: 'GET'});
        setLoadingPageData(false);
        if ( request.status !== '200' ) return
        setTransactions(request.data.items);
        setPageData(request.data);
        SmoothScroll();
    }

    useEffect(() => {
        getTransactions();
    }, [page]);

    const onSuccessAddingTransaction = (data:TransactionType) => {
        setTransactions([...[data], ...transactions]);
    }

    const fetchSuggestions = debounce(async (searchQuery) => {
        const request = await ApiRequest({endpoint: `/transaction?searchParams=${searchQuery}`, method: 'GET'});
        if ( request.status !== '200' ) return
        setTransactions(request.data.items);
        setPageData(request.data);
        SmoothScroll();
      }, 300);

      useEffect(() => {
        search ? fetchSuggestions(search) : getTransactions();
        return fetchSuggestions.cancel;
      }, [search]);

      const deleteTransaction = async (uuid:string) => {
        console.error(uuid);
        const request = await ApiRequest({endpoint: `transaction/${uuid}/delete`, method: 'POST'});
        if ( request.status !== '200' ) {
            ErrorFlash(request.message);
            return;
        }
        getTransactions();
        SuccessFlash("Transaction deleted successfully");
      }

    return (
        <AuthLayout pageLoader={pageLoader}>
            <div className="page-outer">
                <div className="flex items-center justify-between">
                    <h1 className="page-title">Transactions</h1>
                    <div className="lg:w-1/3">
                            <InputField
                                label="Search by issuer name"
                                labelClass="hidden"
                                type="search"
                                placeholder="Search by issuer name"
                                value={search}
                                onChangeInput={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    <div className="flex justify-evenly">
                        <AddTransaction buttonDesign={<AddButtonDesign/>} onSuccess={onSuccessAddingTransaction} onLoading={(state:boolean) => setPageLoader(state)}/>
                    </div>
                </div>
                <div className='relative overflow-x-auto'>
                    <table className='mt-5 text-tertiary text-[14px] w-full relative z-10'>
                        <thead>
                            <tr className='bg-[#F8F8F8]'>
                                <td className="px-3 py-3">Client/Issuer</td>
                                <td className="px-6 py-3">Type</td>
                                <td className="px-6 py-3">Security</td>
                                <td className="px-6 py-3 text-center">Series</td>
                                <td className="px-6 py-3">Size</td>
                                <td className="px-6 py-3">Regulator</td>
                                <td className="px-6 py-3">Date</td>
                                <td className="px-6 py-3">Role</td>
                                <td className="px-6 py-3">Actions</td>
                            </tr>
                        </thead>
                        <tbody className="relative">
                        {transactions.map((transaction, i) => (
                            <tr key={i} className='border-b border-[#E6EAF0]'>
                                <td className="px-3 py-3">{transaction.issuer}​</td>
                                <td className="px-6 py-3">{transaction.type}</td>
                                <td className="px-6 py-3">{transaction.security ?? <Minus size={18}/>}</td>
                                <td className="px-6 py-3 text-center">{transaction.series ?? <Minus size={18}/>}</td>
                                <td className="px-6 py-3">
                                    {getCurrency(transaction.transactionSizeCurrency)}{numberWithCommas(transaction.transactionSize)}
                                </td>
                                <td className="px-6 py-3">{transaction.regulatorQuasi ?? <Minus size={18}/>}</td>
                                <td className="px-6 py-3">{transaction.completionIssueDate ? moment(transaction.completionIssueDate).format('DD MMM YYYY') : <Minus size={18}/>}</td>
                                <td className="px-6 py-3 capitalize">{transaction.role ?? <Minus size={18}/>}</td>
                                <td className="px-6 py-3">
                                    <div className="flex items-center justify-between">
                                        <EditTransaction
                                            buttonDesign={<EditButtonDesign/>}
                                            transaction={transaction}
                                            onSuccess={() => getTransactions()}
                                            onLoading={(state:boolean) => setPageLoader(state)}
                                        />
                                       <div>
                                       <DefaultModal
                                            buttonTriggerClass=""
                                            buttonTriggerClassIcon={<span>
                                                <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12.5 3H10.25V2.25C10.25 1.78587 10.0656 1.34075 9.73744 1.01256C9.40925 0.684374 8.96413 0.5 8.5 0.5H5.5C5.03587 0.5 4.59075 0.684374 4.26256 1.01256C3.93437 1.34075 3.75 1.78587 3.75 2.25V3H1.5C1.30109 3 1.11032 3.07902 0.96967 3.21967C0.829018 3.36032 0.75 3.55109 0.75 3.75C0.75 3.94891 0.829018 4.13968 0.96967 4.28033C1.11032 4.42098 1.30109 4.5 1.5 4.5H1.75V13C1.75 13.3315 1.8817 13.6495 2.11612 13.8839C2.35054 14.1183 2.66848 14.25 3 14.25H11C11.3315 14.25 11.6495 14.1183 11.8839 13.8839C12.1183 13.6495 12.25 13.3315 12.25 13V4.5H12.5C12.6989 4.5 12.8897 4.42098 13.0303 4.28033C13.171 4.13968 13.25 3.94891 13.25 3.75C13.25 3.55109 13.171 3.36032 13.0303 3.21967C12.8897 3.07902 12.6989 3 12.5 3ZM5.25 2.25C5.25 2.1837 5.27634 2.12011 5.32322 2.07322C5.37011 2.02634 5.4337 2 5.5 2H8.5C8.5663 2 8.62989 2.02634 8.67678 2.07322C8.72366 2.12011 8.75 2.1837 8.75 2.25V3H5.25V2.25ZM10.75 12.75H3.25V4.5H10.75V12.75ZM6.25 6.5V10.5C6.25 10.6989 6.17098 10.8897 6.03033 11.0303C5.88968 11.171 5.69891 11.25 5.5 11.25C5.30109 11.25 5.11032 11.171 4.96967 11.0303C4.82902 10.8897 4.75 10.6989 4.75 10.5V6.5C4.75 6.30109 4.82902 6.11032 4.96967 5.96967C5.11032 5.82902 5.30109 5.75 5.5 5.75C5.69891 5.75 5.88968 5.82902 6.03033 5.96967C6.17098 6.11032 6.25 6.30109 6.25 6.5ZM9.25 6.5V10.5C9.25 10.6989 9.17098 10.8897 9.03033 11.0303C8.88968 11.171 8.69891 11.25 8.5 11.25C8.30109 11.25 8.11032 11.171 7.96967 11.0303C7.82902 10.8897 7.75 10.6989 7.75 10.5V6.5C7.75 6.30109 7.82902 6.11032 7.96967 5.96967C8.11032 5.82902 8.30109 5.75 8.5 5.75C8.69891 5.75 8.88968 5.82902 9.03033 5.96967C9.17098 6.11032 9.25 6.30109 9.25 6.5Z" fill="#0099CE"/>
                                                </svg>
                                                </span>}
                                                modalButtonTriggerText=""
                                                description="Are you sure you want to delete this transaction?"
                                                onConfirmationAction={deleteTransaction}
                                                args={transaction.uuid}
                                        />
                                       </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    {transactions.length === 0 && <p className="label">Your transactions will be displayed here</p>}
                </div>
                <div className="flex items-center justify-between mt-6">
                    <div className="flex items-center space-x-2 font-[400]">
                        <span>Show:</span>
                        <select className="flex px-2 py-1 border rounded">
                            <option value="20">20</option>
                            <option value="35">35</option>
                            <option value="50">50</option>
                        </select>
                        <span>per page</span>
                    </div>
                    <div className="flex space-x-3">
                        {page > 1 && <button onClick={() => setPage(page - 1)} type="button" className="font-[600] text-[14px] pryBtnOutline border-[#E7E7E7] text-black hover:bg-transparent hover:text-black">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.5305 18.9693C15.6002 19.039 15.6554 19.1217 15.6932 19.2128C15.7309 19.3038 15.7503 19.4014 15.7503 19.4999C15.7503 19.5985 15.7309 19.6961 15.6932 19.7871C15.6554 19.8781 15.6002 19.9609 15.5305 20.0306C15.4608 20.1002 15.3781 20.1555 15.287 20.1932C15.196 20.2309 15.0984 20.2503 14.9999 20.2503C14.9013 20.2503 14.8037 20.2309 14.7127 20.1932C14.6216 20.1555 14.5389 20.1002 14.4692 20.0306L6.96923 12.5306C6.8995 12.4609 6.84418 12.3782 6.80644 12.2871C6.76869 12.1961 6.74927 12.0985 6.74927 11.9999C6.74927 11.9014 6.76869 11.8038 6.80644 11.7127C6.84418 11.6217 6.8995 11.539 6.96923 11.4693L14.4692 3.9693C14.61 3.82857 14.8008 3.74951 14.9999 3.74951C15.1989 3.74951 15.3898 3.82857 15.5305 3.9693C15.6712 4.11003 15.7503 4.30091 15.7503 4.49993C15.7503 4.69895 15.6712 4.88982 15.5305 5.03055L8.56017 11.9999L15.5305 18.9693Z" fill="#888888"/>
                            </svg>
                            <span>Previous</span>
                        </button>}
                        {pageData.hasMorePages && <button onClick={() => setPage(page + 1)} type="button" className="font-[600] text-[14px] pryBtnOutline border-[#E7E7E7] text-black hover:bg-transparent hover:text-black">
                            <span>Next</span>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.0306 12.5306L9.53055 20.0306C9.46087 20.1002 9.37815 20.1555 9.2871 20.1932C9.19606 20.2309 9.09847 20.2503 8.99993 20.2503C8.90138 20.2503 8.8038 20.2309 8.71276 20.1932C8.62171 20.1555 8.53899 20.1002 8.4693 20.0306C8.39962 19.9609 8.34435 19.8781 8.30663 19.7871C8.26892 19.6961 8.24951 19.5985 8.24951 19.4999C8.24951 19.4014 8.26892 19.3038 8.30663 19.2128C8.34435 19.1217 8.39962 19.039 8.4693 18.9693L15.4396 11.9999L8.4693 5.03055C8.32857 4.88982 8.24951 4.69895 8.24951 4.49993C8.24951 4.30091 8.32857 4.11003 8.4693 3.9693C8.61003 3.82857 8.80091 3.74951 8.99993 3.74951C9.19895 3.74951 9.38982 3.82857 9.53055 3.9693L17.0306 11.4693C17.1003 11.539 17.1556 11.6217 17.1933 11.7127C17.2311 11.8038 17.2505 11.9014 17.2505 11.9999C17.2505 12.0985 17.2311 12.1961 17.1933 12.2871C17.1556 12.3782 17.1003 12.4609 17.0306 12.5306Z" fill="#888888"/>
                            </svg>
                        </button>}
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
}

const AddButtonDesign = () => {
    return (
        <button className="pryBtn w-fit" type="button">
            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.3125 10C18.3125 10.2486 18.2137 10.4871 18.0379 10.6629C17.8621 10.8387 17.6236 10.9375 17.375 10.9375H11.4375V16.875C11.4375 17.1236 11.3387 17.3621 11.1629 17.5379C10.9871 17.7137 10.7486 17.8125 10.5 17.8125C10.2514 17.8125 10.0129 17.7137 9.83709 17.5379C9.66127 17.3621 9.5625 17.1236 9.5625 16.875V10.9375H3.625C3.37636 10.9375 3.1379 10.8387 2.96209 10.6629C2.78627 10.4871 2.6875 10.2486 2.6875 10C2.6875 9.75136 2.78627 9.5129 2.96209 9.33709C3.1379 9.16127 3.37636 9.0625 3.625 9.0625H9.5625V3.125C9.5625 2.87636 9.66127 2.6379 9.83709 2.46209C10.0129 2.28627 10.2514 2.1875 10.5 2.1875C10.7486 2.1875 10.9871 2.28627 11.1629 2.46209C11.3387 2.6379 11.4375 2.87636 11.4375 3.125V9.0625H17.375C17.6236 9.0625 17.8621 9.16127 18.0379 9.33709C18.2137 9.5129 18.3125 9.75136 18.3125 10Z" fill="white"/>
            </svg>
            <span>New Transaction</span>
        </button>
    );
}

const EditButtonDesign = () => {
    return (
        <span className="cursor-pointer"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.5306 3.46961L12.5306 1.46961C12.4609 1.39969 12.3782 1.34422 12.287 1.30636C12.1958 1.26851 12.0981 1.24902 11.9994 1.24902C11.9007 1.24902 11.8029 1.26851 11.7118 1.30636C11.6206 1.34422 11.5378 1.39969 11.4681 1.46961L5.46813 7.46961C5.32797 7.61059 5.24951 7.80145 5.25 8.00024V10.0002C5.25 10.1992 5.32902 10.3899 5.46967 10.5306C5.61032 10.6712 5.80109 10.7502 6 10.7502H8C8.09852 10.7503 8.19609 10.731 8.28714 10.6933C8.37818 10.6557 8.46092 10.6005 8.53063 10.5309L14.5306 4.53086C14.6004 4.46121 14.6557 4.37849 14.6934 4.28745C14.7312 4.1964 14.7506 4.0988 14.7506 4.00024C14.7506 3.90168 14.7312 3.80408 14.6934 3.71303C14.6557 3.62199 14.6004 3.53927 14.5306 3.46961ZM12 3.06274L12.9375 4.00024L12.25 4.68774L11.3125 3.75024L12 3.06274ZM7.6875 9.25024H6.75V8.31274L10.25 4.81274L11.1875 5.75024L7.6875 9.25024ZM14.25 8.78587V13.0002C14.25 13.3318 14.1183 13.6497 13.8839 13.8841C13.6495 14.1185 13.3315 14.2502 13 14.2502H3C2.66848 14.2502 2.35054 14.1185 2.11612 13.8841C1.8817 13.6497 1.75 13.3318 1.75 13.0002V3.00024C1.75 2.66872 1.8817 2.35078 2.11612 2.11636C2.35054 1.88194 2.66848 1.75024 3 1.75024H7.21438C7.41329 1.75024 7.60405 1.82926 7.74471 1.96991C7.88536 2.11056 7.96438 2.30133 7.96438 2.50024C7.96438 2.69915 7.88536 2.88992 7.74471 3.03057C7.60405 3.17122 7.41329 3.25024 7.21438 3.25024H3.25V12.7502H12.75V8.78587C12.75 8.58695 12.829 8.39619 12.9697 8.25553C13.1103 8.11488 13.3011 8.03587 13.5 8.03587C13.6989 8.03587 13.8897 8.11488 14.0303 8.25553C14.171 8.39619 14.25 8.58695 14.25 8.78587Z" fill="#0099CE"/>
        </svg>
        </span>
    );
}

export default Transactions